import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import {
  Media,
  MobileStateMediaItem,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import { AppDispatch, RootState } from '../../redux/Store';
import {
  addMediaLabelToImage,
  deleteMediaLabel,
  getScriptSave,
  getTaskState,
  getTasksData,
} from '../../redux/reducer/coreScriptReducer';
import { ImageMetadata } from '../../types/ui';
import CustomTypography from '../../ui/atoms/CustomTypography';
import MultiKebab from '../../ui/organisms/MenuItems/MultiKebab';
import IconCopy from '../../assets/icons/IconCopy';
import IconRemove from '../../assets/icons/IconRemove';
import useImages from '../MainComponent/hooks/useImages';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import useQueryObject from '../../hooks/useQueryObject';
import Notifications from '../../ui/molecules/toast/Notifications';
import { getTaskMediaItems, groupSavedMediaByTask } from './taskMedia';

const keyToString = (value?: string): string => value?.toLanguagePack() ?? '';

// The human-readable label shown under a tile. State items carry DisplayText;
// fall back to the translated Label key when it's missing (e.g. labels seeded
// only from saved media).
const labelText = (item: MobileStateMediaItem): string =>
  item.DisplayText || keyToString(item.Label);

type TaskMediaTileProps = {
  item: MobileStateMediaItem;
  onClick: () => void;
  onDropImages: (label: string, images: ImageMetadata[]) => void;
  disabled: boolean;
  menuOptions: any[];
};

// Web equivalent of safeview-mobile's SurveyLabelSelectionItem: a square
// thumbnail (or placeholder), the label name, and a Have/Min badge colored by
// completion. Doubles as a drop target so a gallery image dragged onto it is
// associated to this task + label, and exposes a kebab menu to remove the
// image from the task or clone it to another label/task.
const TaskMediaTile = ({
  item,
  onClick,
  onDropImages,
  disabled,
  menuOptions,
}: TaskMediaTileProps) => {
  const showBadge = (item.Min ?? 0) > 0;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'image',
      canDrop: () => !disabled,
      drop: (dragged: { images?: ImageMetadata[] }) => {
        const images = Array.isArray(dragged?.images) ? dragged.images : [];
        onDropImages(item.Label, images);
      },
      collect: (monitor) => ({ isOver: !!monitor.isOver() && !disabled }),
    }),
    [item.Label, onDropImages, disabled]
  );

  return (
    <Box
      ref={drop}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      <Box sx={{ position: 'relative', width: '80px' }}>
        {menuOptions.length > 0 && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ position: 'absolute', top: '2px', left: '2px', zIndex: 1 }}
          >
            <MultiKebab
              disabled={disabled}
              options={menuOptions}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
            />
          </Box>
        )}
        <Box
          sx={{
            width: '80px',
            height: '80px',
            borderRadius: '6px',
            border: isOver ? '2px dashed #1D6EC9' : '1px solid #E3E3E3',
            backgroundColor: '#F4F6FA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {item.Thumb ? (
            <img
              src={item.Thumb}
              alt={labelText(item)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageOutlinedIcon sx={{ color: '#BDC0C9' }} />
          )}
        </Box>
        {showBadge && (
          <Box
            sx={{
              position: 'absolute',
              top: '-6px',
              right: '0px',
              minWidth: '22px',
              height: '22px',
              padding: '0 4px',
              borderRadius: '11px',
              backgroundColor:
                (item.Have ?? 0) >= (item.Min ?? 0) ? '#53b50a' : '#D32F2F',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Montserrat',
              fontSize: '11px',
              fontWeight: 600,
            }}
          >
            {`${item.Have ?? 0}/${item.Min ?? 0}`}
          </Box>
        )}
      </Box>
      <CustomTypography
        variant='body1'
        sx={{
          marginTop: '4px',
          fontFamily: 'Montserrat',
          fontSize: '10px',
          fontWeight: 500,
          color: '#1C6EC9',
          textAlign: 'center',
          lineHeight: '12px',
          width: '100%',
        }}
      >
        {labelText(item)}
      </CustomTypography>
    </Box>
  );
};

type TaskMediaViewProps = {
  taskId: string;
  onSelectLabel?: (label: string) => void;
};

/**
 * TaskMediaView renders the per-task label image tiles, mirroring
 * safeview-mobile's TaskQuestionMediaView: a horizontal strip of label tiles
 * derived from the task's media state merged with the saved media tagged to
 * this task (saveData.Media items whose TaskId matches). Dragging a gallery
 * image onto a tile associates it to this task + label; the per-tile kebab menu
 * removes the image from the task or clones it to another label/task.
 */
const TaskMediaView = ({ taskId, onSelectLabel }: TaskMediaViewProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const saveData = useSelector(getScriptSave);
  const taskState = useSelector(getTaskState);
  const tasksData = useSelector(getTasksData);
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );
  const uploadedImageDetails = useSelector(
    (state: RootState) => state.gallery.uploadedImageDetails
  );
  const { processImageCloneRequest } = useImages();
  const { labels: scriptLabels, mediaList: scriptMediaList } = useMediaLabels();
  const { cloneAllowed } = useQueryObject();

  // Map media GUID -> browser-viewable URL for thumbnails.
  const thumbByGuid = useMemo(() => {
    const map: Record<string, string> = {};
    for (const image of uploadedImageDetails ?? []) {
      if (image?.guid && image?.webFileName) {
        map[image.guid] = image.webFileName;
      }
    }
    return map;
  }, [uploadedImageDetails]);

  const mediaItems = useMemo(() => {
    const savedByTask = groupSavedMediaByTask(saveData?.Media);
    return getTaskMediaItems(
      savedByTask,
      taskState,
      taskId,
      (guid, fallback) => thumbByGuid[guid] ?? fallback
    );
  }, [saveData?.Media, taskState, taskId, thumbByGuid]);

  // GUIDs of the saved media tagged to this task, grouped by label, so a
  // remove/clone can act on exactly this task + label's images.
  const guidsByLabel = useMemo(() => {
    const map: Record<string, string[]> = {};
    const savedByTask = groupSavedMediaByTask(saveData?.Media);
    for (const media of savedByTask[taskId] ?? []) {
      const label = media.Label?.[0];
      if (!label) {
        continue;
      }
      (map[label] ??= []).push(media.Guid);
    }
    return map;
  }, [saveData?.Media, taskId]);

  // Targets a tile's image can be cloned to: every other task's labels (tagged
  // with that task's id) plus the script media labels (no task id).
  const cloneTargets = useMemo(() => {
    const targets: {
      id: string;
      label: string;
      targetLabel: string;
      targetTaskId?: string;
    }[] = [];

    for (const [otherTaskId, otherTaskState] of Object.entries(
      taskState ?? {}
    )) {
      const list = otherTaskState?.media?.List ?? {};
      const taskName =
        tasksData?.[otherTaskId]?._taskName ||
        tasksData?.[otherTaskId]?.DisplayName ||
        otherTaskId;
      for (const label of Object.keys(list)) {
        targets.push({
          id: `task:${otherTaskId}:${label}`,
          label: `${keyToString(taskName)}: ${list[label]?.DisplayText || keyToString(label)}`,
          targetLabel: label,
          targetTaskId: otherTaskId,
        });
      }
    }

    for (const label of scriptLabels ?? []) {
      // Tasks run on the mobile state shape, so the script media list items are
      // MobileStateMediaItem (which carry DisplayText) at runtime.
      const scriptItem = scriptMediaList?.[label] as unknown as
        | MobileStateMediaItem
        | undefined;
      targets.push({
        id: `script:${label}`,
        label: scriptItem?.DisplayText || keyToString(label),
        targetLabel: label,
      });
    }

    return targets.sort((a, b) => a.label.localeCompare(b.label));
  }, [taskState, tasksData, scriptLabels, scriptMediaList]);

  // Associate dropped gallery images with this task + label. Mirrors the gallery
  // drop flow (addMediaLabelToImage), but passes taskId so the engine tags the
  // media with TaskId and regenerates this task's media state.
  const handleDropImages = useCallback(
    (label: string, images: ImageMetadata[]) => {
      const media: Media[] = images
        .filter((image) => image.guid && image.webFileName)
        .map((image) => ({ Guid: image.guid, ThumbUri: image.webFileName }));
      if (!label || media.length === 0) {
        return;
      }
      dispatch(addMediaLabelToImage({ label, media, taskId }));
    },
    [dispatch, taskId]
  );

  // Remove this task + label's image(s) from the task.
  const handleRemoveFromTask = useCallback(
    (label: string) => {
      const guids = guidsByLabel[label] ?? [];
      if (guids.length === 0) {
        return;
      }
      dispatch(deleteMediaLabel({ label, guids, taskId }));
    },
    [dispatch, guidsByLabel, taskId]
  );

  // Clone this task + label's image(s) to another label or task.
  const handleCloneTo = useCallback(
    (label: string, targetLabel: string, targetTaskId?: string) => {
      const guids = guidsByLabel[label] ?? [];
      if (guids.length === 0) {
        return;
      }
      processImageCloneRequest(targetLabel, guids, targetTaskId).catch(
        (error: any) => Notifications(error?.message ?? 'Clone failed', 0)
      );
    },
    [guidsByLabel, processImageCloneRequest]
  );

  const buildMenuOptions = useCallback(
    (label: string) => {
      if (isReadOnly || (guidsByLabel[label] ?? []).length === 0) {
        return [];
      }
      return [
        {
          id: 'remove',
          label: 'Remove from task',
          prefixIcon: <IconRemove />,
          onClick: () => handleRemoveFromTask(label),
        },
        {
          id: 'clone',
          label: 'Clone to label / task',
          disabled: !cloneAllowed,
          prefixIcon: <IconCopy />,
          subOptions: cloneTargets
            .filter(
              (target) =>
                !(
                  target.targetTaskId === taskId && target.targetLabel === label
                )
            )
            .map((target) => ({
              id: target.id,
              label: target.label,
              onClick: () =>
                handleCloneTo(label, target.targetLabel, target.targetTaskId),
            })),
        },
      ];
    },
    [
      isReadOnly,
      guidsByLabel,
      cloneAllowed,
      cloneTargets,
      taskId,
      handleRemoveFromTask,
      handleCloneTo,
    ]
  );

  if (mediaItems.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(10, 1fr)',
        justifyItems: 'center',
        gap: '8px',
        padding: '12px 4px 16px',
      }}
    >
      {mediaItems.map((item) => (
        <TaskMediaTile
          key={item.Label}
          item={item}
          disabled={isReadOnly}
          onDropImages={handleDropImages}
          onClick={() => onSelectLabel?.(item.Label)}
          menuOptions={buildMenuOptions(item.Label)}
        />
      ))}
    </Box>
  );
};

export default TaskMediaView;
